import React, { useState, useRef, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { FaBars, FaTimes, FaUserGraduate, FaSignInAlt, FaSignOutAlt, FaInbox, FaNewspaper, FaProjectDiagram, FaChevronDown, FaCog, FaUserPlus, FaUserShield, FaSun, FaMoon, FaBell, FaBrain, FaGamepad, FaExternalLinkAlt, FaExchangeAlt, FaBook } from 'react-icons/fa';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import { useLanguage } from '../contexts/LanguageContext';

const Navbar = () => {
  const [open, setOpen] = useState(false);
  const [adminOpen, setAdminOpen] = useState(false);
  const [triviaOpen, setTriviaOpen] = useState(false);
  const adminRef = useRef(null);
  const triviaRef = useRef(null);
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const { isAdmin, signOut, authLoading } = useAuth();
  const { dark, toggleTheme } = useTheme();
  const { lang, toggleLang, t } = useLanguage();

  // Close dropdowns on outside click
  useEffect(() => {
    const handler = (e) => {
      if (adminRef.current && !adminRef.current.contains(e.target)) setAdminOpen(false);
      if (triviaRef.current && !triviaRef.current.contains(e.target)) setTriviaOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  // Close dropdowns on route change
  useEffect(() => { setAdminOpen(false); setTriviaOpen(false); setOpen(false); }, [pathname]);

  const links = [
    { to: '/', label: t('nav.home') },
    { to: '/alumni', label: t('nav.directory') },
    { to: '/proyek', label: t('nav.projects') },
    { to: '/berita', label: t('nav.news') },
    { to: '/statistik', label: t('nav.statistics') },
    { to: '/pkm-corner', label: t('nav.pkmCorner') },
    { to: '/tentang', label: t('nav.about') },
  ];

  const triviaLinks = [
    { to: '/trivia/kuis', label: t('trivia.tabQuiz'), emoji: '🧠' },
    { to: '/trivia/tebak-alumni', label: t('trivia.tabGuess'), emoji: '🕵️' },
    { to: '/trivia/catch-me', label: t('trivia.tabCatch'), emoji: '✋🏆' },
  ];

  const adminLinks = [
    { to: '/admin/requests', label: t('nav.alumniRequests'), icon: <FaInbox /> },
    { to: '/admin/edit-requests', label: t('nav.editRequests'), icon: <FaExchangeAlt /> },
    { to: '/admin/news', label: t('nav.manageNews'), icon: <FaNewspaper /> },
    { to: '/admin/projects', label: t('nav.manageProjects'), icon: <FaProjectDiagram /> },
    { to: '/add-alumni', label: t('nav.addAlumni'), icon: <FaUserPlus /> },
    { to: '/admin/notifications', label: t('nav.emailNotifications'), icon: <FaBell /> },
    { to: '/admin/quiz', label: t('nav.manageQuiz'), icon: <FaBrain /> },
    { to: '/admin/pkm-bank', label: t('nav.managePkmBank'), icon: <FaBook /> },
  ];

  const isAdminPath = pathname.startsWith('/admin') || pathname === '/add-alumni';

  const active = (path) =>
    pathname === path
      ? 'text-gold-400 font-semibold'
      : 'text-gray-200 hover:text-white';

  const handleLogout = async () => {
    await signOut();
    navigate('/');
  };

  return (
    <nav className="bg-gradient-to-r from-primary-900 via-primary-800 to-primary-700 shadow-lg sticky top-0 z-50 dark:from-dark-950 dark:via-dark-900 dark:to-dark-900 dark:border-b dark:border-dark-700">
      {/* Auth loading progress bar */}
      {authLoading && (
        <div className="absolute top-0 left-0 w-full h-1 z-50 overflow-hidden">
          <div className="h-full bg-gradient-to-r from-gold-400 via-gold-500 to-amber-400 animate-progress-bar rounded-full"></div>
        </div>
      )}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-2 group shrink-0">
            <FaUserGraduate className="text-gold-400 text-2xl group-hover:rotate-12 transition-transform" />
            <span className="text-white font-bold text-xl tracking-tight">
              PIMNAS <span className="text-gold-400">Alumni</span>
            </span>
          </Link>

          {/* Desktop links */}
          <div className="hidden lg:flex items-center gap-0.5">
            {links.map((l) => (
              <Link
                key={l.to}
                to={l.to}
                className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors ${active(l.to)}`}
              >
                {l.label}
              </Link>
            ))}

            {/* Trivia Dropdown */}
            <div className="relative" ref={triviaRef}>
              <button
                onClick={() => setTriviaOpen(!triviaOpen)}
                className={`flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                  pathname.startsWith('/trivia')
                    ? 'text-gold-400 font-semibold'
                    : 'text-gray-200 hover:text-white'
                }`}
              >
                <FaGamepad className="text-xs" />
                {t('nav.trivia')}
                <FaChevronDown className={`text-[10px] transition-transform ${triviaOpen ? 'rotate-180' : ''}`} />
              </button>

              {triviaOpen && (
                <div className="absolute left-0 mt-2 w-56 bg-white dark:bg-dark-800 rounded-xl shadow-xl border border-gray-100 dark:border-dark-700 py-2 animate-fade-in z-50">
                  <div className="px-3 py-2 border-b border-gray-100 dark:border-dark-700 mb-1">
                    <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider flex items-center gap-1.5">
                      <FaGamepad className="text-primary-500" /> {t('nav.triviaGames')}
                    </p>
                  </div>
                  {triviaLinks.map((l) => (
                    <a
                      key={l.to}
                      href={l.to}
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={() => setTriviaOpen(false)}
                      className="flex items-center gap-2.5 px-3 py-2.5 text-sm transition-colors mx-1 rounded-lg text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-dark-700 hover:text-primary-600 dark:hover:text-primary-400 group"
                    >
                      <span className="text-lg">{l.emoji}</span>
                      <span className="flex-1">{l.label}</span>
                      <FaExternalLinkAlt className="text-[10px] text-gray-300 dark:text-gray-500 group-hover:text-primary-400 transition-colors" />
                    </a>
                  ))}
                </div>
              )}
            </div>

            {/* Theme Toggle */}
            <button
              onClick={toggleTheme}
              className="relative ml-1 w-9 h-9 rounded-xl flex items-center justify-center text-gray-300 hover:text-white hover:bg-white/10 transition-all duration-300"
              aria-label="Toggle dark mode"
              title={dark ? t('nav.lightMode') : t('nav.darkMode')}
            >
              <span className={`absolute transition-all duration-300 ${dark ? 'opacity-0 rotate-90 scale-0' : 'opacity-100 rotate-0 scale-100'}`}>
                <FaMoon className="text-sm" />
              </span>
              <span className={`absolute transition-all duration-300 ${dark ? 'opacity-100 rotate-0 scale-100' : 'opacity-0 -rotate-90 scale-0'}`}>
                <FaSun className="text-sm text-gold-400" />
              </span>
            </button>

            {/* Language Toggle */}
            <button
              onClick={toggleLang}
              className="ml-1 px-2.5 h-9 rounded-xl flex items-center justify-center text-xs font-bold text-gray-300 hover:text-white hover:bg-white/10 transition-all duration-300 border border-white/10 hover:border-white/20"
              aria-label="Toggle language"
              title={lang === 'id' ? 'Switch to English' : 'Ganti ke Bahasa Indonesia'}
            >
              {lang === 'id' ? 'EN' : 'ID'}
            </button>

            {isAdmin ? (
              <>
                {/* Admin Dropdown */}
                <div className="relative ml-1" ref={adminRef}>
                  <button
                    onClick={() => setAdminOpen(!adminOpen)}
                    className={`flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                      isAdminPath
                        ? 'bg-primary-600/50 text-gold-400 font-semibold'
                        : 'text-gray-200 hover:text-white hover:bg-white/10'
                    }`}
                  >
                    <FaCog className="text-xs" />
                    {t('nav.admin')}
                    <FaChevronDown className={`text-[10px] transition-transform ${adminOpen ? 'rotate-180' : ''}`} />
                  </button>

                  {adminOpen && (
                    <div className="absolute right-0 mt-2 w-56 bg-white dark:bg-dark-800 rounded-xl shadow-xl border border-gray-100 dark:border-dark-700 py-2 animate-fade-in">
                      <div className="px-3 py-2 border-b border-gray-100 mb-1">
                        <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider flex items-center gap-1.5">
                          <FaUserShield className="text-primary-500" /> {t('nav.adminPanel')}
                        </p>
                      </div>
                      {adminLinks.map((l) => (
                        <Link
                          key={l.to}
                          to={l.to}
                          className={`flex items-center gap-2.5 px-3 py-2.5 text-sm transition-colors mx-1 rounded-lg ${
                            pathname === l.to
                              ? 'bg-primary-50 text-primary-700 font-semibold'
                              : 'text-gray-700 hover:bg-gray-50 hover:text-primary-600'
                          }`}
                        >
                          <span className={`text-sm ${pathname === l.to ? 'text-primary-600' : 'text-gray-400'}`}>{l.icon}</span>
                          {l.label}
                        </Link>
                      ))}
                      <div className="border-t border-gray-100 mt-1 pt-1 mx-1">
                        <button
                          onClick={handleLogout}
                          className="flex items-center gap-2.5 w-full px-3 py-2.5 text-sm text-red-500 hover:bg-red-50 rounded-lg transition-colors"
                        >
                          <FaSignOutAlt className="text-sm" /> {t('nav.logout')}
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              </>
            ) : (
              <>
                <Link
                  to="/ajukan-alumni"
                  className="ml-2 bg-emerald-500 hover:bg-emerald-600 text-white text-sm font-semibold py-2 px-4 rounded-lg shadow transition-all hover:shadow-lg"
                >
                  {t('nav.applyNow')}
                </Link>
                <Link
                  to="/login"
                  className="ml-2 text-gray-300 hover:text-white text-sm font-medium flex items-center gap-1 transition-colors"
                >
                  <FaSignInAlt /> {t('nav.admin')}
                </Link>
              </>
            )}
          </div>

          {/* Mobile toggle */}
          <div className="flex items-center gap-2 lg:hidden">
            <button
              onClick={toggleLang}
              className="px-2.5 h-9 rounded-xl flex items-center justify-center text-xs font-bold text-gray-300 hover:text-white hover:bg-white/10 transition-all border border-white/10"
              aria-label="Toggle language"
            >
              {lang === 'id' ? 'EN' : 'ID'}
            </button>
            <button
              onClick={toggleTheme}
              className="w-9 h-9 rounded-xl flex items-center justify-center text-gray-300 hover:text-white hover:bg-white/10 transition-all relative"
              aria-label="Toggle dark mode"
            >
              <span className={`absolute transition-all duration-300 ${dark ? 'opacity-0 rotate-90 scale-0' : 'opacity-100 rotate-0 scale-100'}`}>
                <FaMoon className="text-sm" />
              </span>
              <span className={`absolute transition-all duration-300 ${dark ? 'opacity-100 rotate-0 scale-100' : 'opacity-0 -rotate-90 scale-0'}`}>
                <FaSun className="text-sm text-gold-400" />
              </span>
            </button>
            <button
              onClick={() => setOpen(!open)}
              className="text-white text-xl focus:outline-none"
              aria-label="Toggle menu"
            >
              {open ? <FaTimes /> : <FaBars />}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile menu */}
      {open && (
        <div className="lg:hidden bg-primary-800/95 dark:bg-dark-900/95 backdrop-blur border-t border-primary-600 dark:border-dark-700 pb-4 max-h-[80vh] overflow-y-auto">
          <div className="py-1">
            {links.map((l) => (
              <Link
                key={l.to}
                to={l.to}
                onClick={() => setOpen(false)}
                className={`block px-5 py-3 text-sm font-medium transition-colors ${active(l.to)}`}
              >
                {l.label}
              </Link>
            ))}

            {/* Trivia games in mobile — open in new tab */}
            <div className="mx-4 my-2 border-t border-primary-600 dark:border-dark-700"></div>
            <div className="px-5 py-2">
              <p className="text-xs font-semibold text-primary-300 uppercase tracking-wider flex items-center gap-1.5 mb-1">
                <FaGamepad className="text-gold-400" /> {t('nav.triviaGames')}
              </p>
            </div>
            {triviaLinks.map((l) => (
              <a
                key={l.to}
                href={l.to}
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => setOpen(false)}
                className="flex items-center gap-2.5 px-5 py-3 text-sm font-medium text-gray-200 hover:text-white hover:bg-primary-700/30 transition-colors"
              >
                <span className="text-lg">{l.emoji}</span>
                <span className="flex-1">{l.label}</span>
                <FaExternalLinkAlt className="text-[10px] text-primary-400" />
              </a>
            ))}
          </div>

          {isAdmin ? (
            <>
              <div className="mx-4 my-2 border-t border-primary-600 dark:border-dark-700"></div>
              <div className="px-5 py-2">
                <p className="text-xs font-semibold text-primary-300 uppercase tracking-wider flex items-center gap-1.5 mb-1">
                  <FaUserShield className="text-gold-400" /> {t('nav.adminPanel')}
                </p>
              </div>
              {adminLinks.map((l) => (
                <Link
                  key={l.to}
                  to={l.to}
                  onClick={() => setOpen(false)}
                  className={`flex items-center gap-2.5 px-5 py-3 text-sm font-medium transition-colors ${
                    pathname === l.to
                      ? 'text-gold-400 font-semibold bg-primary-700/50'
                      : 'text-gray-200 hover:text-white hover:bg-primary-700/30'
                  }`}
                >
                  <span className={`text-sm ${pathname === l.to ? 'text-gold-400' : 'text-primary-400'}`}>{l.icon}</span>
                  {l.label}
                </Link>
              ))}
              <div className="mx-4 mt-3">
                <button
                  onClick={() => { handleLogout(); setOpen(false); }}
                  className="flex items-center justify-center gap-2 w-full text-red-300 hover:text-red-200 hover:bg-red-500/10 text-sm font-medium py-2.5 rounded-lg transition-colors"
                >
                  <FaSignOutAlt /> {t('nav.logout')}
                </button>
              </div>
            </>
          ) : (
            <div className="px-4 pt-3 space-y-2">
              <Link
                to="/ajukan-alumni"
                onClick={() => setOpen(false)}
                className="block text-center bg-emerald-500 hover:bg-emerald-600 text-white text-sm font-semibold py-2.5 px-4 rounded-lg"
              >
                {t('nav.applyNow')}
              </Link>
              <Link
                to="/login"
                onClick={() => setOpen(false)}
                className="block text-center text-gray-300 hover:text-white text-sm font-medium py-2"
              >
                <FaSignInAlt className="inline mr-1" /> {t('nav.adminLogin')}
              </Link>
            </div>
          )}
        </div>
      )}
    </nav>
  );
};

export default Navbar;
